import { type FC, type ReactNode, useCallback, useMemo, useState } from 'react'

import { useOpenableControls } from '../../generic-hooks'
import { useGlobalConfigContext } from '../../global-config'
import {
  gtmBuildPropsForCartIntermediatePage,
  type ProductPageGtmDataProps,
} from '../../gtm/'
import { useNowGetZipCodeFromLocalStorage } from '../../now'
import { CartIntermediateDrawer } from '../components/CartIntermediateDrawer'
import { CartIntermediateContext } from '../context/CartIntermediateContext'
import { gtmDataLayerGetCartIntermediateUrlData } from '../gtm/gtmDataLayerGetCartIntermediateUrlData'
import {
  type CartIntermediateContextValue,
  type CartIntermediateDrawerMessages,
  type CartIntermediateEntryObject,
} from '../model/CartIntermediateContextValue'

type CartIntermediateContextProviderProps = {
  children: ReactNode
  pdpGtmData?: ProductPageGtmDataProps
}

export const CartIntermediateContextProvider: FC<
  CartIntermediateContextProviderProps
> = ({ children, pdpGtmData }) => {
  const [quantity, setQuantity] = useState<number>(0)
  const [entries, setEntries] = useState<CartIntermediateEntryObject[] | null>(
    null,
  )
  const [isDataSuccessfullyAddedToCart, setIsDataSuccessfullyAddedToCart] =
    useState<boolean>(false)

  const [drawerMessages, setDrawerMessages] =
    useState<CartIntermediateDrawerMessages>({
      infoMessage: null,
      titleMessage: null,
    })

  const cartIntermediateOpenableControls = useOpenableControls()
  const specialOffersCollapsibleControls = useOpenableControls()
  const { language, shopEnv, tenant, tenantAndEnv } = useGlobalConfigContext()
  const zipCode = useNowGetZipCodeFromLocalStorage()
  const gtmDataLayerCartIntermediateUrlData =
    gtmDataLayerGetCartIntermediateUrlData({
      entries,
      language,
      quantity,
      tenantAndEnv,
      zipCode,
    })
  const cartIntermediateDataLayerInitialData =
    gtmBuildPropsForCartIntermediatePage({
      language,
      shopEnv,
      tenant,
      tenantAndEnv,
    })

  const openCartIntermediateDrawerWithData = useCallback(
    (
      products: CartIntermediateEntryObject[],
      messages: CartIntermediateDrawerMessages,
      entryQuantity = 1,
    ): void => {
      setEntries(products)
      setQuantity(entryQuantity)
      setDrawerMessages(messages)
      cartIntermediateOpenableControls.handleOpen()
    },
    [
      setEntries,
      setQuantity,
      setDrawerMessages,
      cartIntermediateOpenableControls,
    ],
  )
  const resetCartIntermediateContext = (): void => {
    setQuantity(0)
    setEntries(null)
    setDrawerMessages({
      infoMessage: null,
      titleMessage: null,
    })
  }

  const contextValue = useMemo(
    (): CartIntermediateContextValue => ({
      cartIntermediateDataLayerInitialData,
      cartIntermediateOpenableControls,
      drawerMessages,
      entries,
      gtmDataLayerCartIntermediateUrlData,
      isDataSuccessfullyAddedToCart,
      openCartIntermediateDrawerWithData,
      pdpGtmData,
      quantity,
      resetCartIntermediateContext,
      setDrawerMessages,
      setEntries,
      setIsDataSuccessfullyAddedToCart,
      setQuantity,
      specialOffersCollapsibleControls,
    }),
    [
      entries,
      drawerMessages,
      quantity,
      cartIntermediateOpenableControls,
      openCartIntermediateDrawerWithData,
      specialOffersCollapsibleControls,
      cartIntermediateDataLayerInitialData,
      isDataSuccessfullyAddedToCart,
      gtmDataLayerCartIntermediateUrlData,
      pdpGtmData,
    ],
  )

  return (
    <CartIntermediateContext.Provider value={contextValue}>
      {children}
      <CartIntermediateDrawer />
    </CartIntermediateContext.Provider>
  )
}
